<template>
  <div class="dialog-add-account" v-loading="loading">
    <div class="dialog-add-account__title" v-html="title"></div>
    <div v-if="!isEditable" class="dialog-add-account__desc">
      {{ $t('referral_data.info') }}
    </div>
    <div v-if="!isEditable" class="dialog-add-account__desc">
      {{ $t('referral_data.conditions')
      }}<a href="/program-description" class="outside-link" target="_blank">{{
        $t('referral_data.desc_link')
      }}</a>
    </div>
    <form class="dialog-add-account__form">
      <div class="dialog-add-account__form-title">
        {{ $t('referral_data.requisites') }}
      </div>
      <div class="dialog-add-account__form-block">
        <div class="dialog-add-account__form-row">
          <InnInput
            :value="form.inn"
            :fio="form.fio"
            :error="errors.inn"
            :fio-error="errors.fio"
            :server-errors="authErrors"
            :inn-disabled="isEditable"
            :is-validated="isCorrectInn"
            :loading="loading"
            @input="form.inn = $event"
            @update:fio="form.fio = $event"
            @check="handleCheckInn"
            @error="errors.inn = $event"
            @validation-reset="isCorrectInn = false"
          />
        </div>
      </div>
      <template v-if="isCorrectInn">
        <BankDetailsForm
          :bank-data="bankFormData"
          :errors="bankErrors"
          :server-errors="authErrors"
          :locale="locale"
          @update:bankData="handleBankDataUpdate"
        />
        <div v-if="!isEditable" class="dialog-add-account__form-agree">
          <label class="input__item-checkbox">
            <input type="checkbox" value="'all'" required v-model="form.offer_agreement_accepted" />
            <span class="input__item-checkbox__check">
              <img src="@/assets/icons/icon-done.svg" />
            </span>
            <span class="input__item-checkbox__info">
              <span class="input__item-checkbox__title">
                {{ $t('business_account_data.agree_with') }}
                <a href="https://profitads.ru/dogovor-oferty-ref/" target="_blank">{{
                  $t('business_account_data.agent_offer_contract')
                }}</a>
              </span>
            </span>
          </label>
          <div
            v-if="errors.offer_agreement_accepted"
            class="form-input__error"
            v-text="errors.offer_agreement_accepted"
          />
        </div>
        <div class="dialog-add-account__form-buttons">
          <ButtonElement :text="$t('button_props.save')" @click-button="saveData" />
          <ButtonElement
            :text="$t('button_props.cancel')"
            view="empty"
            @click-button="closeDialog"
          />
        </div>
      </template>
    </form>
  </div>
</template>

<script>
import formValidate from '../../mixin/formValidate';
import ButtonElement from '../../components/elements/ButtonElement';
import BankDetailsForm from '../forms/BankDetailsForm.vue';
import InnInput from '../forms/InnInput.vue';
import axios from 'axios';
import { setReactiveLocalStorage } from '@/utils/localStorage';
import { METRIKA_GOALS, reachMetrikaGoal } from '@/utils/metrika';

export default {
  name: 'ReferralData',
  components: {
    ButtonElement,
    BankDetailsForm,
    InnInput,
  },
  mixins: [formValidate],
  data() {
    return {
      locale: process.env.VUE_APP_LOCALE,
      loading: false,
      isCorrectInn: false, //change before commit
      form: {
        inn: '',
        bic: '',
        iban: '',
        bank_address: '',
        bank_name: '',
        offer_agreement_accepted: '',
        fio: '',
      },
      errors: {
        inn: '',
        bank_name: '',
        agree_terms_contract: '',
        bic: '',
        bank_address: '',
        offer_agreement_accepted: '',
        fio: '',
      },
    };
  },
  computed: {
    activeAccount() {
      return this.$store.state.Auth.activeAccount;
    },
    user() {
      return this.$store.state.Auth.user;
    },
    role() {
      return this.$store.state.Auth.role;
    },
    editAgent() {
      return this.$store.state.Referrals.editAgent;
    },
    isEditable() {
      return this.$store.state.Dialog.isEditable;
    },
    title() {
      return this.isEditable ? this.$t('referral_data.title_edit') : this.$t('referral_data.title');
    },
    authErrors() {
      return this.$store.state.Auth.errors || null;
    },
    bankFormData: {
      get() {
        return {
          bank_name: this.form.bank_name || '',
          bic: this.form.bic || '',
          iban: this.form.iban || '',
          bank_address: this.form.bank_address || '',
        };
      },
      set(value) {
        this.form.bank_name = value.bank_name || '';
        this.form.bic = value.bic || '';
        this.form.iban = value.iban || '';
        this.form.bank_address = value.bank_address || '';
      },
    },
    bankErrors() {
      return {
        bank_name: this.errors.bank_name || '',
        bic: this.errors.bic || '',
        iban: this.errors.iban || '',
        bank_address: this.errors.bank_address || '',
      };
    },
  },
  mounted() {
    this.isCorrectInn = this.isEditable ? true : false;
    this.form.bank_name = this.editAgent ? this.editAgent.bank_name : '';
    this.form.bic = this.editAgent ? this.editAgent.bic : '';
    this.form.iban = this.editAgent ? this.editAgent.iban : '';
    this.form.bank_address = this.editAgent ? this.editAgent.bank_address : '';
    this.form.inn = this.editAgent ? this.editAgent.inn : '';
    this.form.fio = this.editAgent ? this.editAgent.fio : '';
  },
  methods: {
    clearForm() {
      (this.inn = ''),
        (this.form.bic = ''),
        (this.form.iban = ''),
        (this.form.bank_address = ''),
        (this.form.bank_name = ''),
        (this.form.offer_agreement_accepted = false),
        (this.form.fio = '');
    },
    saveData() {
      if (this.isEditable) {
        this.editAccount();
      } else {
        this.addNewAccount();
      }
    },
    editAccount() {
      this.validateForm();
      if (this.isError) {
        return false;
      }
      this.loading = true;
      const data = {};
      // Для менеджера/админа используем обычный токен, для агента - client_token
      const token =
        this.role === 'manager' || this.role === 'admin' || this.role === 'finance'
          ? localStorage.getItem('token')
          : localStorage.getItem('client_token') || localStorage.getItem('token');

      const agentId = this.editAgent
        ? this.editAgent.id
        : this.user.agent
        ? this.user.agent.id
        : null;

      if (!agentId) {
        this.loading = false;
        this.$notify({
          message: 'Ошибка: не найден ID агента',
          type: 'error',
        });
        return;
      }

      data.fio = (this.form.fio || '').trim();
      data.bic = this.form.bic || '';
      data.iban = this.form.iban || '';
      data.bank_name = this.form.bank_name || '';
      data.bank_address = this.form.bank_address || '';
      data.id = agentId;

      this.$store
        .dispatch('Referrals/editReferral', { data, token })
        .then((resp) => {
          // Сохраняем обновленные данные агента
          const updatedAgent = resp.data.data;

          this.loading = false;
          this.closeDialog();
          this.$notify({
            message: this.$t('referral_data.referral_updated'),
            type: 'success',
          });
          // Обновляем данные пользователя/агента через userinfo для полной синхронизации
          const updateToken =
            this.role === 'manager' || this.role === 'admin' || this.role === 'finance'
              ? localStorage.getItem('token')
              : localStorage.getItem('client_token') || localStorage.getItem('token');
          axios({
            url: process.env.VUE_APP_API_URL + '/auth/userinfo',
            method: 'GET',
            headers: { Authorization: `Bearer ${updateToken}` },
          })
            .then((resp) => {
              // Обновляем данные пользователя, но сохраняем агента из обновления, если его нет в ответе
              const userData = resp.data.data;
              const agentInResponse = userData.agent || updatedAgent;

              this.$store.commit('Auth/setData', {
                label: 'user',
                data: {
                  ...userData,
                  agent: agentInResponse,
                },
              });

              // Обновляем editAgent тоже
              if (agentInResponse) {
                this.$store.commit('Referrals/setData', {
                  label: 'editAgent',
                  data: agentInResponse,
                });
              }
              this.clearForm();
            })
            .catch(() => {
              // Если запрос userinfo не удался, все равно обновляем агента из ответа editReferral
              if (updatedAgent && this.user) {
                this.$store.commit('Auth/setData', {
                  label: 'user',
                  data: {
                    ...this.user,
                    agent: updatedAgent,
                  },
                });
              }
              this.clearForm();
            });
        })
        .catch((error) => {
          this.loading = false;
          this.$notify({
            message: this.$t('business_account_data.error'),
            type: 'error',
          });
          console.error('error', error);
        });
    },
    addNewAccount() {
      this.validateForm();
      if (this.isError) {
        return false;
      }
      this.loading = true;
      const data = {};
      const clientToken = localStorage.getItem('client_token');
      // Нормализуем ФИО до сборки payload, чтобы не отправлять пробелы
      this.form.fio = (this.form.fio || '').trim();
      for (let key in this.form) {
        if (this.form[key]) {
          if (key === 'offer_agreement_accepted') {
            data.rules = this.form['offer_agreement_accepted'];
          } else {
            data[key] = this.form[key];
          }
        }
      }
      this.$store
        .dispatch('Referrals/addReferral', { data, token: clientToken })
        .then(() => {
          reachMetrikaGoal(this, METRIKA_GOALS.PARTNER_PROGRAM_FORM);
          this.loading = false;
          this.closeDialog();
          this.$notify({
            message: this.$t('business_account_data.referral_created'),
            type: 'success',
          });
          axios({
            url: process.env.VUE_APP_API_URL + '/auth/userinfo',
            method: 'GET',
            headers: { Authorization: `Bearer ${clientToken}` },
          }).then(() => {
            this.clearForm();
            setReactiveLocalStorage('isOpenReferral', true);
            this.$router.replace({ name: 'ReferralAgentStatistics' });
            window.location.reload();
          });
        })
        .catch((error) => {
          this.loading = false;
          this.$notify({
            message: this.$t('business_account_data.error'),
            type: 'error',
          });
          console.error('error', error);
        });
    },
    validateForm() {
      if (!this.form.offer_agreement_accepted && !this.isEditable) {
        this.errors.offer_agreement_accepted = this.$t('business_account_data.should_agree');
      }
      if (!this.form.inn) {
        this.errors.inn = this.$t('business_account_data.input_inn');
      }
      // if (!this.form.bank_name) {
      //   this.errors.bank_name = this.$t('business_account_data.input_bank')
      // }
      // if (!this.form.bic) {
      //   this.errors.bic = this.$t('business_account_data.input_bank_id')
      // }
      // if (!this.form.iban) {
      //   this.errors.iban = this.$t('business_account_data.input_account_number')
      // }
      // if (!this.form.bank_address) {
      //   this.errors.bank_address = this.$t('business_account_data.input_bank_address')
      // }
      const normalizedFio = (this.form.fio || '').trim();
      if (!normalizedFio) {
        this.errors.fio = this.$t('business_account_data.invalid_full_name');
      } else {
        this.errors.fio = '';
        this.form.fio = normalizedFio;
      }
    },
    closeDialog() {
      this.$store.dispatch('Dialog/closeDialog');
      this.$store.commit('Dialog/setData', { label: 'activeDialog', data: '' });
    },
    handleCheckInn(inn) {
      this.loading = true;
      const data = {
        inn: inn,
      };
      this.$store
        .dispatch('Ad/checkInnReferral', data)
        .then((data) => {
          this.isCorrectInn = true;
          for (const key in data.data) {
            this.form[key] = data.data[key];
          }
          this.loading = false;
        })
        .catch((err) => {
          this.isCorrectInn = false;
          this.errors.inn = err.errors.inn ? err.errors.inn[0] : err.errors;
          this.loading = false;
        });
    },
    removeEmpty(obj) {
      return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k, v != null && v !== '' ? v : 0])
      );
    },
    handleBankDataUpdate(bankData) {
      this.form.bank_name = bankData.bank_name || '';
      this.form.bic = bankData.bic || '';
      this.form.iban = bankData.iban || '';
      this.form.bank_address = bankData.bank_address || '';
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.dialog-add-account {
  .course-percent {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;

    .dialog-add-account__form-item {
      width: calc(50% - 1rem);
      height: unset;
    }
  }

  &__form-type--radio {
    margin-bottom: 1.5rem;
  }

  .button {
    display: inline-block;
  }

  &__form-type {
    label:first-of-type {
      border-right: 1px solid #bfc2c6;

      &.error {
        border-color: #eb5757;
      }
    }
  }
  .outside-link {
    color: var(--primary);
    text-decoration: underline;
  }
}
</style>
